import { useState } from "react";
import { osFetch } from "../api";
import { useOS } from "../OSContext";
import { toast } from "sonner";
import { RotateCcw, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

// One-click restore of the curated investor/demo dataset. Owner/admin only and
// only in the demo organization (backend enforces both). Reloads afterwards so
// the dashboard, attention badges and notification count all refresh.
export function DemoResetButton() {
  const { ctx } = useOS();
  const [busy, setBusy] = useState(false);
  const isAdmin = (ctx?.roles || []).some((r) => r === "owner" || r === "admin");
  if (!ctx || !isAdmin || ctx.org_id !== "arcacentum") return null;

  const reset = async () => {
    setBusy(true);
    toast.info("Restoring the curated demo dataset…");
    try {
      const r = await osFetch("/demo/reset", { method: "POST" });
      toast.success(`Demo restored — ${r.unread} notifications, ${r.entities.products} products, ${r.entities.projects} projects.`);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(e.message || "Reset failed");
      setBusy(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="demo-reset-btn" disabled={busy}
          className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] rounded px-1 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Reset demo
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="demo-reset-dialog" style={{ backgroundColor: "#0a0a0c" }} className="border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Restore the curated demo dataset?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            Resets Mission Control to the investor-walkthrough baseline: the curated portfolio, attention items and 28 unread notifications. Any changes made in this demo organization (read state, test data) are replaced. Other organizations are unaffected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="demo-reset-cancel" className="bg-transparent border-white/15 text-slate-200 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="demo-reset-confirm" onClick={reset} className="bg-[#0055FF] hover:bg-[#0044dd] text-white">Reset demo data</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
